import { useAuthRoles } from "../../hooks/useAuthRoles";
import { Loading } from "../../components/Loading";
import { useState, useEffect } from "react";
import { useInvitationService } from "../../services/invitation.service";
import { Navigate } from "react-router-dom";

/** Redirects authenticated users to dashboard. Use for landing page so logged-in users skip it. */
export const AuthenticatedRedirectWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { isAuthenticated, isLoading } = useAuthRoles();

  if (isLoading) {
    return <Loading />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Protected route wrapper
export const ProtectedRouteWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { isAuthenticated, isLoading } = useAuthRoles();

  if (isLoading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Admin route wrapper
export const AdminRouteWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { isAdmin, isLoading } = useAuthRoles();

  if (isLoading) {
    return <Loading />;
  }

  if (!isAdmin) {
    return <Navigate to="/notauthorized" replace />;
  }

  // The AdminPage itself will check if user is admin and redirect if not
  return <>{children}</>;
};

export const InvitedRouteWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { isAuthenticated, isLoading, isAdmin } = useAuthRoles();
  const { isInvited, getWorkspaceStatus } = useInvitationService();

  const [userIsInvited, setUserIsInvited] = useState<boolean | null>(null);
  const [isSuspended, setIsSuspended] = useState(false);
  const [checkingInvitation, setCheckingInvitation] = useState(true);

  useEffect(() => {
    const checkInvitation = async () => {
      setCheckingInvitation(true);
      try {
        if (isAuthenticated && !isAdmin) {
          const invitationStatus = await isInvited();
          setUserIsInvited(invitationStatus);

          // Check if tenant is suspended
          if (invitationStatus) {
            const status = await getWorkspaceStatus();
            setIsSuspended(status === "suspended");
          }
        } else {
          setUserIsInvited(false);
        }
      } catch (err) {
        console.error("[InvitedRouteWrapper] checkInvitation failed:", err);
        // Token refresh or auth check failed — kick the unauthorized flow so
        // the user is logged out instead of stuck on the Loading spinner.
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("invirtus:auth-unauthorized", {
              detail: { source: "invited-route-check" },
            })
          );
        }
        setUserIsInvited(false);
      } finally {
        setCheckingInvitation(false);
      }
    };
    checkInvitation();
  }, [isInvited, getWorkspaceStatus, isAuthenticated, isAdmin]);

  if (isLoading || checkingInvitation) {
    return <Loading />;
  }

  // Admins always pass through
  if (isAdmin) {
    return <>{children}</>;
  }

  if (isSuspended) {
    return <Navigate to="/suspended" replace />;
  }

  if (!userIsInvited) {
    // Self-signup users without a plan → send them through the new
    // unified signup page so they can pick (the page detects auth state
    // and skips creds-collection if already authenticated).
    return <Navigate to="/signup?plan=free_trial" replace />;
  }

  return <>{children}</>;
};
